import { getAssessmentType, isAssessmentPaid } from '@apps/materiality-tracker/utils';
import { MATERIALITY_TRACKER_MESSAGES } from '@apps/materiality-tracker/constants';
import { DashboardSection } from '@g17eco/molecules/dashboard';
import CardGrid, {
  CardGridButtonProps,
  CardGridItem,
  CardGridItemProps,
  CardGridViewMode,
} from '@features/survey-scope/CardGrid';
import { ROUTES } from '@constants/routes';
import { PACK, QUESTION } from '@constants/terminology';
import { SearchBox } from '@g17eco/molecules/search';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { MetricGroup } from '@g17eco/types/metricGroup';
import { generateUrl } from '@routes/util';
import { assessmentTypeLabels } from '@utils/materiality';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getRootOrg } from '@selectors/initiative';
import { useAppSelector } from '@reducers/index';
import { getCustomMetricGroups } from './utils';
import { CompanyTrackerIntegrationCard } from '../integration-card/CompanyTrackerIntegrationCard';
import { isMaterialityTracker } from '@utils/apps';
import type { RootInitiativeData } from '@g17eco/types/initiative';
import { IntegrationCardVariant } from '../integration-card/types';
import { CardGridButtonType } from '@features/custom-metrics/CustomMetricGroups';
import { Button } from 'reactstrap';
import { useUnlockAssessment } from '@apps/materiality-tracker/hooks/useUnlockAssessment';
import { BlockingLoader } from '@g17eco/atoms/loader';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';

interface Props {
  initiativeId: string;
  metricGroups: MetricGroup[];
}

interface Filters {
  searchText: string;
  assessmentType: string;
}

const allOption = { value: 'all', label: 'All assessments' };

const assessmentTypeOptions = [
  ...Object.entries(assessmentTypeLabels).map(([value, label]) => ({ value, label })),
  allOption,
];

const DashboardToolbar = ({ children, className }: { children: React.ReactNode; className?: string }) => {
  return (
    <div
      className={`d-flex align-items-center justify-content-between gap-2 py-2 px-3 background-ThemeBgToolbar rounded ${className}`}
    >
      {children}
    </div>
  );
};

const isPureMaterialityTracker = (rootOrg: RootInitiativeData | undefined): rootOrg is RootInitiativeData => {
  return rootOrg ? isMaterialityTracker(rootOrg) : false;
};

export const MetricGroupDashboard = ({ initiativeId, metricGroups }: Props) => {
  const [filters, setFilters] = useState<Filters>({ searchText: '', assessmentType: allOption.value });
  const history = useHistory();
  const rootOrg = useAppSelector(getRootOrg);
  const { handleUnlock, isUnlocking } = useUnlockAssessment();

  const handleChangeFilters = (newFilters: Partial<Filters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const filteredMetricGroups = metricGroups.filter((metricGroup) => {
    const assessmentType = getAssessmentType(metricGroup.survey);
    const isMatchingAssessmentType =
      filters.assessmentType === allOption.value || filters.assessmentType === assessmentType;

    const isMatchingSearchText =
      !filters.searchText || metricGroup.groupName.toLowerCase().includes(filters.searchText.toLowerCase());

    return isMatchingAssessmentType && isMatchingSearchText;
  });

  const handleEditMetricGroup = (groupId: string) => {
    const url = generateUrl(ROUTES.MATERIALITY_TRACKER_MODULES, { initiativeId, groupId });
    history.push(url);
  };

  const getCardButtons: (metricGroup: MetricGroup) => CardGridButtonProps[] = (metricGroup) => {
    const survey = metricGroup.survey;
    if (survey && !isAssessmentPaid(survey)) {
      return [
        {
          type: CardGridButtonType.Edit,
          button: (
            <Button color='primary' onClick={() => handleUnlock(survey)} size='xs'>
              <i className='fal fa-dollar-sign mr-2' />
              Unlock now
            </Button>
          ),
        },
      ];
    }

    // Check if outputs are locked by staff
    const isOutputsLocked = Boolean(survey?.outputsLockedDate);

    return [
      metricGroup.source
        ? {
            type: CardGridButtonType.Edit,
            button: (
              <SimpleTooltip text={isOutputsLocked ? MATERIALITY_TRACKER_MESSAGES.OUTPUTS_LOCKED : ''}>
                <Button
                  color='secondary'
                  size='xs'
                  onClick={() => handleEditMetricGroup(metricGroup._id)}
                  disabled={isOutputsLocked}
                >
                  <i className='fal fa-pencil mr-2' />
                  Edit {PACK.SINGULAR}
                </Button>
              </SimpleTooltip>
            ),
          }
        : {
            type: CardGridButtonType.Edit,
            button: (
              <Button disabled size='xs'>
                {survey?.completedDate ? 'Regenerate scores to view' : 'Complete assessment to view'}
              </Button>
            ),
          },
    ];
  };

  const metricGroupCards = getCustomMetricGroups({
    metricGroups: filteredMetricGroups,
    getButtons: getCardButtons,
  });

  const isPureMT = isPureMaterialityTracker(rootOrg);

  return (
    <>
      {isUnlocking ? <BlockingLoader /> : null}
      <h3 className='ml-3'>
        Custom {QUESTION.SINGULAR} {PACK.PLURAL}
      </h3>
      <DashboardSection>
        <div>
          Below are the custom {PACK.PLURAL} generated from your completed assessments. {PACK.CAPITALIZED_PLURAL} are
          automatically created per assessment, with {QUESTION.PLURAL} tailored to your company's results based on key
          factors such as material topics, industry, company size, and location. To adjust mapped {QUESTION.PLURAL},
          click 'Edit {PACK.CAPITALIZED_SINGULAR}'.
        </div>
        <DashboardToolbar className='my-3'>
          <SearchBox
            handleOnChange={(e) => handleChangeFilters({ searchText: e.target.value })}
            searchText={filters.searchText}
            placeholder={`Search for custom ${PACK.SINGULAR}`}
            classNames={{ wrapper: 'w-100' }}
          />
          <SelectFactory
            selectType={SelectTypes.SingleSelect}
            options={assessmentTypeOptions}
            value={assessmentTypeOptions.find(({ value }) => value === filters.assessmentType)}
            onChange={(option) => handleChangeFilters({ assessmentType: option?.value ?? 'all' })}
            backgroundColor='var(--theme-BgExtralight)'
            className='col-4'
          />
        </DashboardToolbar>
        <CardGrid viewLayout={CardGridViewMode.gallery}>
          {[
            isPureMT ? (
              <div
                key={`scope-cardgriditem-integration-${rootOrg._id}`}
                className='card-grid-item viewLayout-grid border border-ThemeAccentMedium background-ThemeAccentExtralight'
              >
                <CompanyTrackerIntegrationCard initiative={rootOrg} variant={IntegrationCardVariant.Row} />
              </div>
            ) : null,
            ...metricGroupCards.map(({ key, ...cardProps }: CardGridItemProps) => (
              <CardGridItem key={key} {...cardProps} />
            )),
          ]}
        </CardGrid>
      </DashboardSection>
    </>
  );
};
